// =============================================================================
// MeuDriver — Hook: useAchievements
// Busca conquistas disponíveis e desbloqueadas do usuário logado
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { AchievementRow, UserAchievementRow } from '@/types/database'

export interface AchievementWithProgress extends AchievementRow {
  earned: boolean
  earned_at: string | null
}

async function fetchAchievements(userId: string): Promise<AchievementWithProgress[]> {
  const [{ data: all, error: allErr }, { data: earned, error: earnedErr }] = await Promise.all([
    supabase.from('achievements').select('*').eq('active', true).order('condition_value'),
    supabase
      .from('user_achievements')
      .select('achievement_id, earned_at')
      .eq('user_id', userId),
  ])

  if (allErr) throw allErr
  if (earnedErr) throw earnedErr

  const earnedMap = new Map<string, string>(
    (earned ?? []).map((ua) => [ua.achievement_id, ua.earned_at]),
  )

  return (all ?? []).map((a) => ({
    ...a,
    earned: earnedMap.has(a.id),
    earned_at: earnedMap.get(a.id) ?? null,
  }))
}

export function useAchievements() {
  const { user } = useAuth()

  return useQuery<AchievementWithProgress[]>({
    queryKey: ['achievements', user?.id],
    queryFn: () => fetchAchievements(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  })
}

export function useUserXP() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['user-xp', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('xp_points, level, current_streak, longest_streak')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return data as {
        xp_points: number
        level: number
        current_streak: number
        longest_streak: number
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
  })
}
